import { api } from "./api.ts";

import type { AnyType, ServiceType } from "types/DataTypes";

type GetServiceByIdArgs = {
  id: string | number;
};

type CreateServiceArgs = {
  data: AnyType;
};

type PatchServiceArgs = {
  id: string | number;
  data: AnyType;
};

type DeleteServiceArgs = {
  id: string | number;
};

type SendServiceRequestForMaterialsEmailArgs = {
  id: string | number;
  to_email: string;
  subject: string;
  body: string;
  cc_emails?: string[];
};

type GetArchivedServicesArgs = {
  fileId: string | number;
};

type RestoreServiceArgs = {
  id: string | number;
};

export const serviceAPI = api.injectEndpoints({
  endpoints: (build) => ({
    getServices: build.query<ServiceType[], void>({
      query: () => ({
        url: `api/v1/services/`,
        method: "GET",
      }),
    }),
    getServiceById: build.query<ServiceType, GetServiceByIdArgs>({
      query: ({ id }) => ({
        url: `api/v1/services/${id}/`,
        method: "GET",
      }),
    }),
    createService: build.mutation<ServiceType, CreateServiceArgs>({
      query: ({ data }) => ({
        url: `api/v1/services/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { data }) => {
        const fileId =
          typeof data === "object" && data !== null && "file_id" in data
            ? (data as { file_id?: string | number }).file_id
            : undefined;

        return [
          "ServiceAssignee",
          "FileValidation",
          "Files",
          "ArchivedServices",
          ...(fileId
            ? [
                { type: "MSATable" as const, id: `FILE-${fileId}` },
                { type: "CostProjectionTable" as const, id: `FILE-${fileId}` },
                {
                  type: "AmendedReviewScreening" as const,
                  id: `FILE-${fileId}`,
                },
                {
                  type: "AmendedReviewScreeningValidation" as const,
                  id: `FILE-${fileId}`,
                },
              ]
            : []),
        ];
      },
    }),
    patchServiceById: build.mutation<ServiceType, PatchServiceArgs>({
      query: ({ id, data }) => ({
        url: `api/v1/services/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Files"],
    }),
    deleteServiceById: build.mutation<void, DeleteServiceArgs>({
      query: ({ id }) => ({
        url: `api/v1/services/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["Files", "ArchivedServices"],
    }),
    getArchivedServices: build.query<ServiceType[], GetArchivedServicesArgs>({
      query: ({ fileId }) => ({
        url: `api/v1/services/archived/`,
        method: "GET",
        params: { file_id: fileId },
      }),
      providesTags: ["ArchivedServices"],
    }),
    restoreService: build.mutation<ServiceType, RestoreServiceArgs>({
      query: ({ id }) => ({
        url: `api/v1/services/${id}/restore/`,
        method: "POST",
      }),
      invalidatesTags: ["Files", "ArchivedServices", "ServiceAssignee"],
    }),
    sendServiceRequestForMaterialsEmail: build.mutation<
      { request_for_materials_sent_at: string | null },
      SendServiceRequestForMaterialsEmailArgs
    >({
      query: ({ id, ...data }) => ({
        url: `api/v1/services/${id}/send-request-for-materials-email/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Files"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetServiceByIdQuery,
  useCreateServiceMutation,
  usePatchServiceByIdMutation,
  useDeleteServiceByIdMutation,
  useGetServicesQuery,
  useSendServiceRequestForMaterialsEmailMutation,
  useGetArchivedServicesQuery,
  useRestoreServiceMutation,
} = serviceAPI;
